import { FC, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { BranchCode } from "api/risk";
import { CopropertyEligibilityStatus } from "api/coproperty";
import { copropertiesApi } from "services/swagger-api";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { useAsyncResource } from "back-office/shared/hooks/useAsyncResource";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { CLAIM_GUARANTEE_LABELS, claimsSituationSummary } from "./CopropertyClaimsForm";
import { EligibilityVerdict, EligibilityVerdictBanner, buildingStandingLabel, toVerdict } from "./CopropertyFieldControls";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertySection, CopropertyStepLayout } from "./CopropertyStepLayout";
import { useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/** Surface en m² au format français ("4 250 m²"). */
const formatArea = (value: number): string => `${value.toLocaleString("fr-FR")} m²`;

/** Montant en euros sans décimales ("8 500 000 €"). */
const formatAmount = (value: number): string =>
  value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const GateRow: FC<{ title: string; detail: string; route: string; optional?: boolean }> = ({
  title,
  detail,
  route,
  optional = false,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-4 rounded-2xl border bg-white px-4 py-3 shadow">
      {optional ? (
        <span className="h-[15px] w-[15px] flex-none rounded-full border-2 border-gray-300" />
      ) : (
        <Check className="h-[18px] w-[18px] flex-none text-green-700" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-zinc-800">{title}</p>
        <p className="text-[13px] text-gray-500">{detail}</p>
      </div>
      <button
        type="button"
        onClick={() => navigate(route)}
        className="flex-none text-[13px] font-medium text-primary hover:text-primary/80"
      >
        Modifier
      </button>
    </div>
  );
};

const FigureTile: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl border bg-white px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-xl font-bold text-zinc-800">{value}</p>
  </div>
);

/**
 * Synthèse d'éligibilité du dossier MRI, entre la boucle bâtiments et la tarification,
 * dans le cadre commun des sous-étapes : les trois portes (copropriété, questionnaire,
 * bâtiments) revérifiées côté serveur, les chiffres retenus, puis l'entrée en
 * tarification par le bouton d'étape. On n'arrive ici qu'avec un questionnaire éligible
 * (le flux l'impose) ; le verdict copropriété + bâtiments est réévalué à l'affichage —
 * s'il n'est plus éligible, le bandeau l'explique et la tarification reste fermée.
 */
export const MriEligibilitySummaryPage = () => {
  const { riskId, copropertyId, coproperty, isLoading } = useCoproperty();

  const fetchVerdict = useCallback(
    () => copropertiesApi.getCopropertyEligibility({ copropertyId: copropertyId! }).then(toVerdict),
    [copropertyId]
  );
  const { data: verdict, isLoading: isVerdictLoading } = useAsyncResource<EligibilityVerdict | null>(
    fetchVerdict,
    copropertyId != null
  );

  const isEligible = verdict?.status === CopropertyEligibilityStatus.Eligible;

  const branchStepConfig: BranchStepConfig = {
    [BranchCode.Mri]: createStepConfig({
      prev: {
        identifier: MriStepIdentifier.CopropertyClaims,
        route: MRI_ROUTES.copropertyClaims,
        label: "Retour à la sinistralité",
      },
      next: {
        identifier: MriStepIdentifier.Pricing,
        route: MRI_ROUTES.pricing,
        label: "Passer à la tarification",
        disabled: !isEligible,
      },
    }),
  };
  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(branchStepConfig);

  if (riskId && copropertyId == null) {
    return <Navigate to={`${MRI_ROUTES.copropertyRegistration}/${riskId}`} replace />;
  }

  if (isLoading || isVerdictLoading || !coproperty) {
    return <GenericLoading isPageCentered />;
  }

  const buildings = coproperty.buildings ?? [];
  const complements = coproperty.complements;
  const yesNo = (value: boolean | null | undefined): string | false =>
    value == null ? false : value ? "oui" : "non";
  const hasComplements =
    complements != null &&
    (complements.currentPremiumExclTax != null ||
      complements.premiumYear != null ||
      complements.nextGeneralMeetingDate != null ||
      complements.insuredLast36Months != null ||
      complements.coverageInterruptionLast36Months != null ||
      complements.terminationLast36Months != null);
  const claimsHistory = coproperty.claimsHistory;
  const claims = claimsHistory?.claims ?? [];
  const maxFloors = buildings.reduce<number | null>(
    (max, building) => (building.floorsCount != null && (max == null || building.floorsCount > max) ? building.floorsCount : max),
    null
  );

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        <CopropertyStepLayout
          title="Synthèse d'éligibilité"
          message={
            isEligible
              ? "Le dossier est complet et éligible : les trois portes sont validées, vous pouvez passer à la tarification."
              : "Le dossier n'est pas encore éligible — corrigez les points signalés avant la tarification."
          }
          aside={
            <>
              <CopropertyIdentityCard coproperty={coproperty} />
              <CopropertyRecapPanel
                coproperty={coproperty}
                hideIdentity
                showRetainedArea
                checklist={[
                  { label: "Copropriété rattachée", status: "done" },
                  { label: "Questionnaire éligible", status: "done" },
                  { label: "Bâtiments décrits", status: isEligible ? "done" : "blocked" },
                ]}
              />
            </>
          }
        >
          <CopropertySection title="Critères d'éligibilité">
            {isEligible ? (
              <div className="flex flex-col gap-3">
                <GateRow
                  title="Copropriété"
                  detail={[
                    coproperty.greenSpaceArea != null && `Espace vert ${formatArea(coproperty.greenSpaceArea)}`,
                    coproperty.above1500mAltitude === false && "pas de situation en altitude",
                    "surfaces par nature cohérentes",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  route={`${MRI_ROUTES.copropertyRegistration}/${riskId}`}
                />
                <GateRow
                  title="Questionnaire d'appréciation du risque"
                  detail="Aucune exclusion d'environnement"
                  route={`${MRI_ROUTES.copropertySurvey}/${riskId}`}
                />
                <GateRow
                  title="Bâtiments"
                  detail={[
                    `${buildings.length} bâtiment${buildings.length > 1 ? "s" : ""} décrit${buildings.length > 1 ? "s" : ""}`,
                    maxFloors != null && `aucun au-delà de R+7 (max R+${maxFloors})`,
                    "aucun bâtiment vide non muré",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  route={`${MRI_ROUTES.copropertyBuildings}/${riskId}`}
                />
              </div>
            ) : (
              verdict && <EligibilityVerdictBanner verdict={verdict} />
            )}
          </CopropertySection>

          <CopropertySection title="Compléments déclarés">
            <div className="flex flex-col gap-3">
              <GateRow
                title="Contrat en cours et prochaine AG"
                detail={
                  hasComplements
                    ? [
                        complements?.currentPremiumExclTax != null &&
                          `Prime ${complements.currentPremiumExclTax.toLocaleString("fr-FR")} € HT`,
                        complements?.premiumYear != null && `exercice ${complements.premiumYear}`,
                        complements?.nextGeneralMeetingDate != null &&
                          `AG le ${complements.nextGeneralMeetingDate.toLocaleDateString("fr-FR")}`,
                        yesNo(complements?.insuredLast36Months) &&
                          `assuré 36 mois : ${yesNo(complements?.insuredLast36Months)}`,
                        yesNo(complements?.coverageInterruptionLast36Months) &&
                          `interruption : ${yesNo(complements?.coverageInterruptionLast36Months)}`,
                        yesNo(complements?.terminationLast36Months) &&
                          `résiliation : ${yesNo(complements?.terminationLast36Months)}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "Étape passée — aucune information renseignée"
                }
                route={`${MRI_ROUTES.copropertyComplements}/${riskId}`}
                optional={!hasComplements}
              />
              <GateRow
                title="Sinistralité"
                detail={
                  claims.length > 0
                    ? `${claimsSituationSummary(claimsHistory)} · ${claims
                        .map((claim) => (claim.guaranteeCode ? CLAIM_GUARANTEE_LABELS[claim.guaranteeCode] : "?"))
                        .join(", ")}`
                    : claimsSituationSummary(claimsHistory)
                }
                route={`${MRI_ROUTES.copropertyClaims}/${riskId}`}
                optional={claims.length === 0}
              />
            </div>
          </CopropertySection>

          <CopropertySection title="Chiffres retenus">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FigureTile
                label="Surface retenue"
                value={coproperty.retainedArea != null ? formatArea(coproperty.retainedArea) : "—"}
              />
              <FigureTile label="Standing de l'immeuble" value={buildingStandingLabel(coproperty.buildingStanding) ?? "—"} />
              <FigureTile
                label="SMP - Sinistre maximum possible"
                value={coproperty.smp != null ? formatAmount(coproperty.smp) : "—"}
              />
              <FigureTile label="Bâtiments décrits" value={String(buildings.length)} />
            </div>
          </CopropertySection>
        </CopropertyStepLayout>
      </StepPageWrapper>
    </div>
  );
};
